import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { sendErrorEvent } from '@Analytics';
import { OnboardingQuestionConfig, UserPreferencesRequest } from '@Models';
import OnboardingCompletion from '../components/OnboardingCompletion';
import OnboardingStepInput from '../components/OnboardingStepInput';
import OnboardingStepSelect from '../components/OnboardingStepSelect';
import OnboardingStepTextarea from '../components/OnboardingStepTextarea';
import OnboardingTrail, { TrailEntry } from '../components/OnboardingTrail';
import { mapAnswersToRequest } from '../utils/onboardingSteps';

interface OnboardingWizardProps {
  questions: OnboardingQuestionConfig[];
  onComplete: () => void;
  onSkip: () => void;
  onSave: (request: UserPreferencesRequest) => Promise<void>;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ questions, onComplete, onSkip, onSave }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [trailEntries, setTrailEntries] = useState<TrailEntry[]>([]);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showSkipHint, setShowSkipHint] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => setShowSkipHint(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isCompleting) {
        onSkip();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSkip, isCompleting]);

  const handleAnswer = useCallback(
    (value: string | string[]) => {
      const step = questions[currentStep];
      const displayValue = Array.isArray(value) ? value.join(', ') : value;

      setTrailEntries((prev) => [...prev, { step, answer: displayValue }]);
      setAnswers((prev) => ({ ...prev, [step.key]: value }));

      if (currentStep < questions.length - 1) {
        setCurrentStep((prev) => prev + 1);
      } else {
        const finalAnswers = { ...answers, [step.key]: value };
        setIsCompleting(true);
        setSaveError(null);

        onSave(mapAnswersToRequest(finalAnswers, questions)).catch((err) => {
          sendErrorEvent(err);
          setSaveError(t('Agent.OnboardingSaveError'));
        });
      }
    },
    [currentStep, answers, onSave, t, questions],
  );

  const handleCompletionDone = useCallback(() => {
    onComplete();
  }, [onComplete]);

  const renderStep = () => {
    const step = questions[currentStep];

    if (step.type === 'input') {
      return (
        <OnboardingStepInput
          key={step.key}
          question={step.question}
          subtitle={step.subtitle}
          placeholder={step.placeholder}
          onAnswer={handleAnswer}
        />
      );
    }

    if (step.type === 'textarea') {
      return (
        <OnboardingStepTextarea
          key={step.key}
          question={step.question}
          subtitle={step.subtitle}
          placeholder={step.placeholder}
          maxLength={step.maxLength}
          onAnswer={handleAnswer}
        />
      );
    }

    return (
      <OnboardingStepSelect
        key={step.key}
        question={step.question}
        subtitle={step.subtitle}
        options={step.options || []}
        type={step.type}
        maxSelections={step.maxSelections}
        onAnswer={handleAnswer}
      />
    );
  };

  const nameQuestion = questions.find((q) => q.targetField === 'preferred_name');
  const completionName = nameQuestion ? (answers[nameQuestion.key] as string) || '' : '';

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-[102] flex"
      style={{ backdropFilter: 'blur(12px)' }}>
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#0f0f1a]/95" />

      <div className="relative z-10 flex items-center w-full max-w-2xl px-12 md:px-20 py-16">
        <div className="w-full">
          <OnboardingTrail entries={trailEntries} />

          <AnimatePresence mode="wait">
            {isCompleting ? (
              <OnboardingCompletion key="completion" name={completionName} onDone={handleCompletionDone} />
            ) : (
              renderStep()
            )}
          </AnimatePresence>

          {saveError && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-red-400 text-sm mt-4">
              {saveError}
            </motion.p>
          )}
        </div>

        {/* Skip hint */}
        <AnimatePresence>
          {showSkipHint && !isCompleting && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="absolute bottom-8 left-12 md:left-20">
              <span className="text-white/20 text-xs max-lg:hidden">{t('Agent.OnboardingSkipHint')}</span>
              <button
                type="button"
                onClick={onSkip}
                className="text-white/20 text-xs cursor-pointer hover:text-white/40 transition-colors focus:outline-none lg:hidden">
                {t('Agent.OnboardingSkipHintMobile')}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Right empty space — intentional asymmetry */}
      <div className="hidden md:block flex-1" />
    </motion.div>,
    document.body,
  );
};

export default OnboardingWizard;
